import { useState, useRef, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject, updateProject, deleteProject } from '../api/projects'
import { getProjectNetworks } from '../api/commNetworks'
import ElectricalPanels from './ElectricalPanels'

export default function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: networks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['projectNetworks', projectId],
    queryFn: () => getProjectNetworks(Number(projectId!)),
    enabled: !!projectId
  })

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  // Initialize edit form when project loads or modal opens
  useEffect(() => {
    if (project && showEditModal) {
      setEditName(project.name || '')
      setEditDescription(project.description || '')
    }
  }, [project, showEditModal])

  // Update mutation
  const updateProjectMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => 
      updateProject(Number(projectId!), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setShowEditModal(false)
    },
    onError: (error) => {
      console.error('Error updating project:', error)
      alert('Ошибка при обновлении проекта')
    }
  })

  // Delete mutation
  const deleteProjectMutation = useMutation({
    mutationFn: () => deleteProject(Number(projectId!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
    onError: (error) => {
      console.error('Error deleting project:', error)
      alert('Ошибка при удалении проекта')
    }
  })

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editName.trim()) {
      alert('Название проекта не может быть пустым')
      return
    }
    updateProjectMutation.mutate({
      name: editName.trim(),
      description: editDescription.trim() || undefined
    })
  }

  const handleDelete = () => {
    deleteProjectMutation.mutate()
    setShowDeleteModal(false)
  }

  // Close modals on ESC
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showEditModal) setShowEditModal(false)
        if (showDeleteModal) setShowDeleteModal(false)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [showEditModal, showDeleteModal])

  if (projectLoading) {
    return <div className="text-center py-8">Loading project...</div>
  }

  if (!project) {
    return <div className="text-center py-8 text-red-600">Project not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to="/projects" className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Back to Projects
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          <p className="text-gray-600 mt-1">{project.description}</p>
        </div>
        <div className="relative">
          {/* Menu Button */}
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation()
              setIsMenuOpen(!isMenuOpen)
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <span>⚙️ Меню проекта</span>
            <svg
              className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
            >
              <div className="py-2">
                <button
                  onClick={() => {
                    setShowEditModal(true)
                    setIsMenuOpen(false)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  ✏️ Редактировать название
                </button>
                <Link
                  to={`/projects/${projectId}/rooms`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  🏠 Помещения
                </Link>
                <Link
                  to={`/projects/${projectId}/ai`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  🤖 ИИ
                </Link>
                <Link
                  to={`/projects/${projectId}/tasks`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  ✅ Задачи
                </Link>
                <Link
                  to={`/projects/${projectId}/specification`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  📋 Спецификация
                </Link>
                <Link
                  to={`/projects/${projectId}/networks`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  🌐 Сети проекта
                </Link>
                <Link
                  to={`/projects/${projectId}/device-instances`}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  ⚡ Все экземпляры устройств
                </Link>
                <div className="border-t border-gray-200 my-1"></div>
                <button
                  onClick={() => {
                    setShowDeleteModal(true)
                    setIsMenuOpen(false)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  🗑️ Удалить проект
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Electrical Panels Section */}
      <ElectricalPanels />

      {/* Сети проекта */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Сети проекта</h2>
            <p className="text-sm text-gray-500 mt-1">Коммуникационные сети проекта и их структурные схемы</p>
          </div>
          <Link
            to={`/projects/${projectId}/networks`}
            className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
          >
            ⚙️ Управление сетями →
          </Link>
        </div>

        {networksLoading ? (
          <div className="text-center py-6 text-gray-500">Загрузка сетей...</div>
        ) : networks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {networks.map((network) => (
              <div
                key={network.id}
                className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow duration-150 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-gray-900 text-lg line-clamp-1">{network.name}</h3>
                    <code className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded font-mono">
                      {network.code}
                    </code>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem] mb-4">
                    {network.description || <span className="text-gray-400 italic">Нет описания</span>}
                  </p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-400 uppercase font-medium">
                    {network.network_type}
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/projects/${projectId}/networks/${network.id}/interfaces`}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-xs font-semibold transition-colors duration-150 flex items-center gap-1"
                      title="Сетевые интерфейсы устройств"
                    >
                      🔌 Интерфейсы
                    </Link>
                    <Link
                      to={`/projects/${projectId}/networks/${network.id}/print`}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-xs font-semibold transition-colors duration-150 flex items-center gap-1"
                      title="Печать таблицы подключений"
                    >
                      🖨️ Печать
                    </Link>
                    {network.schema_id ? (
                      <Link
                        to={`/projects/${projectId}/schemas/${network.schema_id}`}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-semibold shadow-sm transition-colors duration-150 flex items-center gap-1"
                      >
                        💻 Схема
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-400 italic flex items-center">Без схемы</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 border border-dashed border-gray-200 rounded-lg">
            <div className="text-3xl mb-2">🌐</div>
            <p className="text-sm">В этом проекте ещё не создано ни одной сети.</p>
            <Link
              to={`/projects/${projectId}/networks`}
              className="mt-2 inline-block text-xs text-blue-600 hover:underline font-semibold"
            >
              Добавить первую сеть
            </Link>
          </div>
        )}
      </div>

      {/* Edit Project Modal */}
      {showEditModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-6 pb-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Редактировать проект</h3>
            </div>
            
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название проекта <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateProjectMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateProjectMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      {showDeleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false)
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-6 pb-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-600">Удалить проект</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-700">
                Вы уверены, что хотите удалить проект <strong>"{project.name}"</strong>?
              </p>
              <p className="text-sm text-red-600">
                ⚠️ Это действие нельзя отменить. Все данные проекта будут удалены.
              </p>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteProjectMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteProjectMutation.isPending ? 'Удаление...' : 'Удалить проект'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 